import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../auth-lib';
import { Button, Modal, Skeleton, useToast } from '../../../design-system';
import { money, fmtDate, INV_STATUS } from '../../pages/OrgAdminFinance';
import '../../styles/Finance.css';

// One client's whole financial picture — the drill-down from the client list.
// Every invoice, every receipt, and any standing retainer, in one place.
function ClientFinanceDrawer({ clientId, onClose, onOpenInvoice }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/api/finance/clients/${clientId}`)
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.danger(e.response?.data?.error || 'Could not load that client'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const t = data?.totals;

  return (
    <Modal
      isOpen onClose={onClose} size="lg"
      title={data ? `${data.client.name} — finance` : 'Client finance'}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      {loading || !data ? <Skeleton.Text lines={8} /> : (
        <div className="fin-form">
          {data.client.gstin && <p className="fin-sub">GSTIN {data.client.gstin}</p>}

          <div className="fin-tiles fin-tiles-sm">
            <div className="fin-tile"><span className="fin-tile-label">Billed</span><span className="fin-tile-value">{money(t.billed)}</span></div>
            <div className="fin-tile fin-tile-good"><span className="fin-tile-label">Collected</span><span className="fin-tile-value">{money(t.collected)}</span>
              {t.tdsCredited > 0 && <span className="fin-tile-hint">+{money(t.tdsCredited)} TDS</span>}</div>
            <div className="fin-tile fin-tile-warn"><span className="fin-tile-label">Outstanding</span><span className="fin-tile-value">{money(t.outstanding)}</span></div>
            <div className="fin-tile fin-tile-bad"><span className="fin-tile-label">Overdue</span><span className="fin-tile-value">{money(t.overdue)}</span></div>
          </div>

          {/* Charges are NOT part of "Billed" — a charge is work priced, an
              invoice is a document raised. Its own tile, or the two get read as
              the same money. */}
          {t.unbilledCharges > 0 && (
            <div className="fin-tiles fin-tiles-sm">
              <div className="fin-tile fin-tile-warn">
                <span className="fin-tile-label">Priced, not yet invoiced</span>
                <span className="fin-tile-value">{money(t.unbilledCharges)}</span>
                <span className="fin-tile-hint">charges sitting on tasks</span>
              </div>
            </div>
          )}

          {data.retainers.length > 0 && (
            <>
              <span className="fin-form-label">Standing fees</span>
              {data.retainers.map((r) => (
                <p className="fin-note" key={r.id}>
                  <b>{money(r.amount)}</b> {r.frequency.replace('_', ' ')} — {r.title}
                  {r.lastBilledPeriod && <span className="fin-muted"> · last billed {r.lastBilledPeriod}</span>}
                  {!r.isActive && <span className="fin-chip fin-s-cancelled">stopped</span>}
                </p>
              ))}
            </>
          )}

          {/* Charge by charge, grouped by the task that raised it. This is the
              "why" behind the invoices below — the drawer could previously show
              ₹45,000 billed with no way back to the filings it came from. */}
          {(data.taskCharges || []).length > 0 && (
            <>
              <span className="fin-form-label">Charges on tasks ({t.chargeCount})</span>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Task</th><th>Charge</th><th>Head</th>
                      <th className="fin-num">Amount</th><th>Billed on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.taskCharges.map((tc) => tc.charges.map((c, i) => (
                      <tr key={c.billItemId}>
                        {/* Task named once per group, so a filing with a fee and
                            three OPE lines reads as one thing. */}
                        <td>
                          {i === 0 ? (
                            <>
                              <Link className="fin-link" to={`/org-admin/tasks/${tc.taskId}`}>
                                {tc.taskNumber != null ? `TASK-${String(tc.taskNumber).padStart(4, '0')}` : tc.title}
                              </Link>
                              {tc.period && <span className="fin-code">{tc.period}</span>}
                            </>
                          ) : ''}
                        </td>
                        <td>{c.description || <span className="fin-muted">—</span>}</td>
                        <td className="fin-muted">{c.kind === 'ope' ? 'Reimbursement' : 'Fees'}</td>
                        <td className="fin-num">{money(c.amount)}</td>
                        <td>
                          {c.isBilled ? (
                            <button
                              type="button" className="fin-link fin-link-btn"
                              onClick={() => onOpenInvoice?.(c.invoice.id)}
                            >
                              {c.invoice.number || 'Draft'}
                            </button>
                          ) : (
                            <span className="fin-chip fin-s-draft">
                              {c.invoice?.cancelled ? 'released (cancelled)' : 'not billed'}
                            </span>
                          )}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <span className="fin-form-label">Invoices</span>
          <div className="fin-scroll">
            <table className="fin-table">
              <thead><tr><th>Invoice</th><th>Date</th><th className="fin-num">Total</th><th className="fin-num">Due</th><th>Status</th></tr></thead>
              <tbody>
                {data.invoices.map((i) => {
                  const s = INV_STATUS[i.status];
                  return (
                    <tr key={i.id} className="fin-row-click" onClick={() => onOpenInvoice?.(i.id)}>
                      <td className="fin-nowrap"><b>{i.invoiceNumber || 'Draft'}</b></td>
                      <td className="fin-nowrap fin-muted">{fmtDate(i.invoiceDate)}</td>
                      <td className="fin-num">{money(i.total)}</td>
                      <td className="fin-num">{i.amountDue ? <b>{money(i.amountDue)}</b> : '—'}</td>
                      <td><span className={`fin-pill ${s.cls}`}>{s.label}</span>
                        {i.isOverdue && <span className="fin-chip fin-s-overdue">overdue</span>}</td>
                    </tr>
                  );
                })}
                {data.invoices.length === 0 && <tr><td colSpan={5}><p className="fin-empty">No invoices yet.</p></td></tr>}
              </tbody>
            </table>
          </div>

          {data.payments.length > 0 && (
            <>
              <span className="fin-form-label">Receipts</span>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead><tr><th>Date</th><th className="fin-num">Amount</th><th className="fin-num">TDS</th><th>Mode</th><th>Reference</th></tr></thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="fin-nowrap">{fmtDate(p.receivedOn)}</td>
                        <td className="fin-num">{money(p.amount)}</td>
                        <td className="fin-num fin-muted">{p.tdsAmount ? money(p.tdsAmount) : '—'}</td>
                        <td className="fin-muted">{p.mode.replace('_', ' ')}</td>
                        <td className="fin-muted">{p.reference || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

export default ClientFinanceDrawer;
